import {connect} from 'react-redux';
import {Component} from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router';

const propTypes = {
    children: PropTypes.element.isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string
    }).isRequired,
    localisation: PropTypes.shape({
        language: PropTypes.shape({
            code: PropTypes.string
        })
    }).isRequired,
    trackPage: PropTypes.func.isRequired
};

/*
// pushAnalyticsEvent
// used for anything that cannot be tracked with
// event listeners like 'click' or 'change', or data attributes that change dynamically
*/
export const pushAnalyticsEvent = eventData => {
    const data = {
        event: 'visitor interaction',
        ...eventData
    };
    window?.dataLayer?.push(data);
};

export const calculationEvents = (name, calculation, calculatorPage, phoneNumber = null) => {
    const senderCountryCode = calculatorPage.sendCountryCode.toUpperCase();
    const receiveCountryCode = calculatorPage.info.code.toUpperCase();
    const {
        sendCurrency, receiveCurrency, sendAmount, receiveAmount, brand = ''
    } = calculation;

    return {
        [name]: {
            products: [{
                name: calculatorPage.serviceType.toUpperCase(),
                id: `${senderCountryCode}-${receiveCountryCode}`,
                price: sendAmount,
                brand,
                category: `${senderCountryCode}/${receiveCountryCode}/${sendCurrency}/${receiveCurrency}`,
                variant: (calculatorPage.serviceType.toLowerCase() !== 'atp') ? calculation.exchangeRate : 1,
                quantity: 1,
                dimension14: `${sendCurrency}-${receiveCurrency}`,
                dimension15: phoneNumber && phoneNumber.name,
                metric2: receiveAmount
            }]
        }
    };
};

/*
// pushGAEvent
// calls Google Analytics directly
*/
export const pushGAEvent = ({label, action, category}) => {
    if (window.ga && window.ga.getAll) {
        const tracker = window.ga.getAll()[0];
        if (!tracker) return;
        tracker.send({
            hitType: 'event',
            eventCategory: category,
            eventAction: action,
            eventLabel: label
        });
    }
};

/*
// addAnalyticsAttributes
// returns an object with data attributes
*/
export const addAnalyticsAttributes = (obj, tracking) => {
    obj.className = obj.className ? `${obj.className} track-${tracking.event}` : `track-${tracking.event}`;
    obj['data-track-interaction-name'] = tracking.interactionName;
    obj['data-track-field-name'] = tracking.formTrackingField;
    obj['data-track-name'] = tracking.name;

    return obj;
};

export class Analytics extends Component {
    constructor(props) {
        super(props);
        this.handleEventListeners = this.handleEventListeners.bind(this);
        this.trackPage = props.trackPage.bind({...props});
    }

    componentDidMount() {
        window.WR = window.WR || {};
        window.WR.config = window.WR.config || {};
        if (window.WR.config && window.WR.config.routes && this.trackPage) {
            const trackingData = this.trackPage(window.WR.config.routes);
            window.WR.trackingData = Object.assign(window.WR.trackingData, trackingData);
        }
        this.handleEventListeners('add');
    }

    componentWillUnmount() {
        this.handleEventListeners('remove');
    }

    getEvent(element) {
        const trackingRegex = /track-(click|blur|change)/;
        const [, event] = element.className.match(trackingRegex) || [];
        return event;
    }

    trackEvent(event) {
        const attributes = {
            formTrackingName: 'data-track-form-name',
            formTrackingField: 'data-track-field-name',
            interactionName: 'data-track-interaction-name',
            name: 'data-track-name'
        };
        const {target} = event;
        const data = {
            event: 'visitor interaction',
            interactionName: target.getAttribute(attributes.interactionName)
        };

        const formTrackingField = target.getAttribute(attributes.formTrackingField);
        if (formTrackingField) {
            data.formTrackingField = formTrackingField;
        }

        const {form} = target;
        if (form) {
            data.formTrackingName = form.getAttribute(attributes.formTrackingName);
        }

        const name = target.getAttribute(attributes.name);
        if (name) {
            data.name = name;
        }

        return window.dataLayer.push(data);
    }

    handleEventListeners(listenerAction) {
        const eventSelector = '[class*="track-"]';
        const elementsWithEvents = document.querySelectorAll(eventSelector);

        for (let i = 0; i < elementsWithEvents.length; i += 1) {
            const element = elementsWithEvents[i];
            const eventToTrack = this.getEvent(element);

            if (eventToTrack && element.addEventListener) {
                if (listenerAction === 'add') {
                    element.addEventListener(eventToTrack, this.trackEvent, true);
                } else {
                    element.removeEventListener(eventToTrack, this.trackEvent, true);
                }
            }
        }
    }

    render() {
        const {children} = this.props;
        return children;
    }
}

Analytics.propTypes = propTypes;

export const configureAnalytics = (mapStateToProps, mapDispatchToProps) => connect(
    mapStateToProps,
    mapDispatchToProps
)(Analytics);

export default (mapStateToProps, trackPage) => withRouter(
    configureAnalytics(mapStateToProps, trackPage)
);
